import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRestaurantPublic, pairDevice, isStaffAuthed } from '../menuApi';

/**
 * The screen a restaurant actually reaches: gapgo.redspeed.com.au/<restaurant>.
 *
 * WHY THIS REPLACES THE OLD SIGN-IN
 * ---------------------------------
 * `StaffLogin.js` asked for a name and an 8-character mixed-case access code, in a
 * password field, on a kitchen tablet, every 12 hours — because the code doubled as
 * the restaurant's identifier and therefore had to be globally unique. The URL now
 * carries the identity, so the only thing left to type is six digits, once, ever.
 *
 * This is the shared-appliance model Toast, Square and UberEats Merchant use: the
 * tablet on the pass is paired, not logged in. Whoever is on shift just uses it.
 *
 * DESIGN — GapGo is a RedSpeed product and reads like one
 * ------------------------------------------------------
 * LIGHT, matching the kitchen screen this leads into rather than the guest app.
 * A kitchen display lives on a wall-mounted tablet in a bright room, so the whole
 * restaurant-facing surface is light and the guest's phone stays dark — see the
 * header of pages/kitchenPanelCss.js for the reasoning. Tokens below mirror that
 * file's token block; change one, change both.
 *
 * Two RedSpeed rules this obeys deliberately:
 *   - "red is the spice, not the meal" (packages/brand/colors.js) — exactly one
 *     red element on screen, the primary action.
 *   - no red glows and no gradient fills — that was the rejected "AI-made"
 *     direction. Flat red only.
 */
export default function KitchenPairing() {
  const { restaurantSlug } = useParams();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputs = useRef([]);

  // Already paired? Go straight to the orders. A kitchen that opens its bookmark
  // mid-service should never see a sign-in screen at all.
  useEffect(() => {
    if (isStaffAuthed()) navigate('/kitchen', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getRestaurantPublic(restaurantSlug);
        if (!cancelled) setRestaurant(r);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantSlug]);

  const code = digits.join('');
  const complete = code.length === 6 && !digits.includes('');

  const submit = useCallback(async (value) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      await pairDevice(restaurantSlug, value, deviceLabel());
      navigate('/kitchen', { replace: true });
    } catch (e) {
      setError((e && e.message) || 'Could not set up this screen');
      setDigits(['', '', '', '', '', '']);
      setBusy(false);
      if (inputs.current[0]) inputs.current[0].focus();
    }
  }, [busy, restaurantSlug, navigate]);

  const setDigit = (i, raw) => {
    const v = raw.replace(/\D/g, '');
    if (!v) {
      const next = [...digits]; next[i] = ''; setDigits(next);
      return;
    }
    // Typing fast, or pasting the whole code into the first box, both work.
    const next = [...digits];
    let idx = i;
    for (const c of v.split('')) {
      if (idx > 5) break;
      next[idx] = c;
      idx += 1;
    }
    setDigits(next);
    setError('');
    const focus = Math.min(idx, 5);
    if (inputs.current[focus]) inputs.current[focus].focus();
    const joined = next.join('');
    if (joined.length === 6 && !next.includes('')) submit(joined);
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1] && inputs.current[i - 1].focus();
    }
    if (e.key === 'Enter') submit(code);
  };

  if (loading) {
    return (
      <div style={S.page}>
        <Wordmark />
        <p style={S.muted}>Loading…</p>
      </div>
    );
  }

  // A wrong URL used to redirect to the marketing page, which reads as "GapGo is
  // broken". Saying plainly what happened, and who to ask, is the whole fix.
  if (notFound || !restaurant) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <Wordmark />
          <h1 style={S.title}>We don't recognise that address</h1>
          <p style={S.body}>
            There's no restaurant at <span style={S.mono}>/{restaurantSlug}</span>. Check the
            link on your GapGo card, or ask us and we'll send it again.
          </p>
          <a style={S.btnRed} href="mailto:hello@redspeed.com.au">Email RedSpeed</a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <Wordmark />

        <div style={S.brandRow}>
          <span style={S.emoji}>{restaurant.emoji}</span>
          <div>
            <h1 style={S.title}>{restaurant.name}</h1>
            {restaurant.tagline && <p style={S.tagline}>{restaurant.tagline}</p>}
          </div>
        </div>

        <h2 style={S.h2}>Set up this screen</h2>
        <p style={S.body}>
          Enter the 6-digit code RedSpeed gave you. You only do this once — this
          screen then stays signed in and shows your orders as they arrive.
        </p>

        <div style={S.codeRow}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { inputs.current[i] = el; }}
              style={{
                ...S.digit,
                borderColor: error ? RS.danger : (d ? RS.accent : RS.glassBorder),
              }}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputMode="numeric"
              autoComplete="one-time-code"
              type="tel"
              maxLength={6}
              autoFocus={i === 0}
              disabled={busy}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        {error && <div style={S.error}>⚠ {error}</div>}

        <button
          style={{ ...S.btnRed, opacity: busy || !complete ? 0.45 : 1, width: '100%' }}
          onClick={() => submit(code)}
          disabled={busy || !complete}
        >
          {busy ? 'Setting up…' : 'Set up this screen'}
        </button>

        <p style={S.hint}>
          No code? Ask RedSpeed — <a style={S.link} href="mailto:hello@redspeed.com.au">hello@redspeed.com.au</a>
        </p>
      </div>
      <Footer />
    </div>
  );
}

/**
 * The product's own logo — `Gap` + `Go` in accent, matching LandingPage's
 * `.lg-logo` / `.lg-logo-go` exactly (weight 800, -0.5px). This screen belongs to
 * GapGo; the parent brand is credited in the footer rather than lashed to the logo.
 */
function Wordmark() {
  return (
    <div style={S.wordmark}>
      <span style={{ color: RS.text }}>Gap</span>
      <span style={{ color: RS.accent }}>Go</span>
    </div>
  );
}

/** Endorsement, not co-branding: the RedSpeed wordmark sits inside the sentence. */
function Footer() {
  return (
    <p style={S.footer}>
      A{' '}
      <span style={S.footerBrand}>
        <span style={{ color: RS.textMuted }}>Red</span>
        <span style={{ color: RS.accent }}>Speed</span>
      </span>{' '}
      product
    </p>
  );
}

/** A human-readable name for the device list, so "revoke" is not a guessing game. */
function deviceLabel() {
  const ua = navigator.userAgent || '';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android tablet';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return 'Kitchen screen';
}

/* Light, to match the kitchen this screen leads into.
   Values mirror the token block at the top of pages/kitchenPanelCss.js — change one,
   change both. The brand red is unchanged; only the surface is inverted, and the
   status colours are the darkened variants that pass WCAG AA on white (the dark
   theme's #30D158 / #FF9F0A measure about 2:1 here and are unreadable). */
const RS = {
  bg: '#F4F5F7',
  surface: '#FFFFFF',
  text: '#12141A',
  textMuted: '#565C6B',
  textFaint: '#858B99',
  accent: '#E8001D',
  accentDeep: '#B00016',
  danger: '#E8001D',
  glassBg: '#FFFFFF',
  glassBorder: '#E4E7EC',
  hairline: '#E4E7EC',
  shadow: '0 4px 12px rgba(18,20,26,0.08)',
  font: "var(--font-inter, 'Inter'), -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const S = {
  page: {
    minHeight: '100vh', background: RS.bg,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px', boxSizing: 'border-box',
    fontFamily: RS.font, color: RS.text,
  },
  card: {
    width: '100%', maxWidth: '460px',
    background: RS.glassBg, border: `1px solid ${RS.glassBorder}`,
    borderRadius: '24px', padding: '30px 26px', boxSizing: 'border-box',
    boxShadow: RS.shadow,
  },
  // Matches LandingPage `.lg-logo` exactly so the product reads the same everywhere.
  wordmark: {
    fontSize: '22px', fontWeight: '800', letterSpacing: '-0.5px',
    lineHeight: 1, marginBottom: '26px',
  },
  brandRow: {
    display: 'flex', alignItems: 'center', gap: '14px',
    paddingBottom: '20px', marginBottom: '20px',
    borderBottom: `1px solid ${RS.hairline}`,
  },
  emoji: { fontSize: '34px', lineHeight: 1 },
  title: { margin: 0, fontSize: '24px', fontWeight: '700', color: RS.text, letterSpacing: '-0.02em' },
  tagline: { margin: '3px 0 0', fontSize: '13px', color: RS.textMuted },
  h2: { margin: '0 0 8px', fontSize: '17px', fontWeight: '700', color: RS.text, letterSpacing: '-0.01em' },
  body: { margin: '0 0 22px', fontSize: '14px', lineHeight: 1.6, color: RS.textMuted },
  codeRow: { display: 'flex', gap: '8px', marginBottom: '18px' },
  digit: {
    flex: 1, minWidth: 0, aspectRatio: '3 / 4',
    background: RS.glassBg, border: `1px solid ${RS.glassBorder}`,
    borderRadius: '14px', color: RS.text,
    fontSize: '28px', fontWeight: '700', textAlign: 'center',
    fontFamily: 'inherit', outline: 'none', padding: 0,
    transition: 'border-color 0.15s',
  },
  error: {
    background: 'rgba(232,0,29,0.06)', border: '1px solid rgba(232,0,29,0.22)',
    borderRadius: '11px', padding: '11px 14px',
    fontSize: '13px', color: RS.danger, marginBottom: '14px',
  },
  // The one red element on the screen — RS `.btn-red`, pill, 48px.
  btnRed: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    boxSizing: 'border-box', minHeight: '48px', padding: '0 28px',
    borderRadius: '9999px', background: RS.accent, color: '#fff',
    fontSize: '15px', fontWeight: '600', letterSpacing: '-0.01em',
    border: 'none', cursor: 'pointer', textDecoration: 'none',
    fontFamily: 'inherit', width: '100%',
  },
  muted: { fontSize: '13px', color: RS.textFaint, textAlign: 'center' },
  hint: { margin: '18px 0 0', fontSize: '12px', color: RS.textFaint, textAlign: 'center', lineHeight: 1.6 },
  link: { color: RS.textMuted, textDecoration: 'underline' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: RS.text },
  footer: { fontSize: '11px', color: RS.textFaint, marginTop: '26px' },
  footerBrand: { fontWeight: '700', letterSpacing: '-0.2px' },
};
